import { useCallback, useEffect, useRef, useState } from 'react';

interface UseTypewriterPlaceholderOptions {
	suggestions: readonly string[];
	/** ms per character when typing (default: 60) */
	typeSpeed?: number;
	/** ms per character when deleting (default: 15) */
	deleteSpeed?: number;
	/** ms to pause after fully typed (default: 2000) */
	pauseDuration?: number;
	/** whether to animate (set false when user is typing or submitting) */
	enabled?: boolean;
}

type Phase = 'typing' | 'pausing' | 'deleting';

export function useTypewriterPlaceholder({
	suggestions,
	typeSpeed = 60,
	deleteSpeed = 15,
	pauseDuration = 2000,
	enabled = true,
}: UseTypewriterPlaceholderOptions): string {
	const [display, setDisplay] = useState('');
	const indexRef = useRef(0);
	const charRef = useRef(0);
	const phaseRef = useRef<Phase>('typing');
	const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

	const tick = useCallback(() => {
		const text = suggestions[indexRef.current];
		const phase = phaseRef.current;

		if (phase === 'typing') {
			charRef.current += 1;
			setDisplay(text.slice(0, charRef.current));

			if (charRef.current >= text.length) {
				phaseRef.current = 'pausing';
				timerRef.current = setTimeout(tick, pauseDuration);
			} else {
				timerRef.current = setTimeout(tick, typeSpeed);
			}
		} else if (phase === 'pausing') {
			phaseRef.current = 'deleting';
			timerRef.current = setTimeout(tick, deleteSpeed);
		} else if (phase === 'deleting') {
			charRef.current -= 1;
			setDisplay(text.slice(0, charRef.current));

			if (charRef.current <= 0) {
				indexRef.current = (indexRef.current + 1) % suggestions.length;
				phaseRef.current = 'typing';
				timerRef.current = setTimeout(tick, typeSpeed);
			} else {
				timerRef.current = setTimeout(tick, deleteSpeed);
			}
		}
	}, [suggestions, typeSpeed, deleteSpeed, pauseDuration]);

	useEffect(() => {
		if (!enabled) {
			clearTimeout(timerRef.current);
			return;
		}

		// Reset and start
		indexRef.current = 0;
		charRef.current = 0;
		phaseRef.current = 'typing';
		setDisplay('');
		timerRef.current = setTimeout(tick, typeSpeed);

		return () => clearTimeout(timerRef.current);
	}, [enabled, tick, typeSpeed]);

	return display;
}
